import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../assets/logo1.png';
import { showcaseAPI, API_ORIGIN } from '../../services/api';
import AccessRequestPopup from '../LandingPage/AccessRequestPopup';
import './HomePage.css';

// Public marketing home page ("/"), inspired by streaming-service home pages:
// a cinematic hero that plays course promo videos, curated rows (trending,
// new, most enrolled, ...), hover previews, and a "Continue watching" prompt
// that sends visitors to sign in / sign up. Data: GET /api/showcase.

const HERO_INTERVAL_MS = 9000;

const assetUrl = (url) => (!url ? null : url.startsWith('http') ? url : `${API_ORIGIN}${url}`);
const formatPrice = (p) => (p > 0 ? `₹${Number(p).toLocaleString('en-IN')}` : 'Free');
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ---------------------------------------------------------------------------
// Loading strategy (fastest first screen, also on poor connections):
//  1. hero text + ~1KB blurred placeholder, then the sharp hero image
//  2. featured strip + first course row
//  3. other rows mount only as the visitor scrolls near them
//  4. hero promo video only after the page has loaded, on a good connection,
//     while the hero is on screen - otherwise a "Watch preview" button
// ---------------------------------------------------------------------------
const SAVE_DATA = typeof navigator !== 'undefined' && !!(navigator.connection && navigator.connection.saveData);
// Phones: no background autoplay in the hero (saves mobile data); promos
// still play on tap or when a course is opened.
const IS_PHONE = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 760px)').matches;
// Hero image size: 1600px only for large / high-density screens, else 960px.
const HERO_WIDTH = typeof window !== 'undefined' && window.innerWidth * (window.devicePixelRatio || 1) > 1700 ? 1600 : 960;

// "Good" network = the browser reports a 4G-class connection without data
// saver. Browsers that don't report it (Safari) count as good on larger
// screens and slow on phones.
const isGoodNetwork = () => {
  const c = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (c && c.effectiveType) {
    return !c.saveData && c.effectiveType === '4g' && (c.downlink == null || c.downlink >= 1.5);
  }
  return !IS_PHONE;
};

const useGoodNetwork = () => {
  const [good, setGood] = useState(isGoodNetwork);
  useEffect(() => {
    const c = typeof navigator !== 'undefined' ? navigator.connection : null;
    if (!c || !c.addEventListener) return undefined;
    const onChange = () => setGood(isGoodNetwork());
    c.addEventListener('change', onChange);
    return () => c.removeEventListener('change', onChange);
  }, []);
  return good;
};

// True shortly after the window 'load' event (all first-screen images done).
const usePageSettled = (extraMs = 600) => {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    let t;
    const done = () => { t = setTimeout(() => setSettled(true), extraMs); };
    if (document.readyState === 'complete') done();
    else window.addEventListener('load', done, { once: true });
    return () => { clearTimeout(t); window.removeEventListener('load', done); };
  }, [extraMs]);
  return settled;
};

// Resized thumbnail (server-side WEBP, ~40KB instead of multi-MB originals).
// The filename is part of the URL so a re-uploaded thumbnail busts the cache.
const thumbUrl = (course, width) => {
  if (!course.thumbnailUrl || !course.id) return null;
  const v = encodeURIComponent(course.thumbnailUrl.split('?')[0].split('/').pop());
  return `${API_ORIGIN}/api/showcase/thumb/${course.courseId || course.id}?w=${width}&v=${v}`;
};

// Thumbnail over a branded fallback (gradient + title). Tries the resized
// thumbnail, then the original upload, then keeps the fallback. With
// `placeholder`, a ~1KB blurred version shows instantly while it loads.
const Poster = ({ course, className = '', width = 480, priority = false, placeholder = false }) => {
  const sources = [thumbUrl(course, width), assetUrl(course.thumbnailUrl)].filter(Boolean);
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const src = sources[attempt];
  const blur = placeholder ? thumbUrl(course, 48) : null;
  return (
    <div className={`hp-poster hp-poster-wrap ${className}`}>
      <div className="hp-poster-fallback"><span>{course.title}</span></div>
      {blur && !loaded && <img className="hp-poster-blur" src={blur} alt="" aria-hidden="true" fetchpriority="high" />}
      {src && (
        <img
          key={src}
          className={`hp-poster-img ${loaded ? 'is-loaded' : ''}`}
          src={src}
          alt={course.title}
          loading={priority ? 'eager' : 'lazy'}
          fetchpriority={priority ? 'high' : 'low'}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(false); setAttempt((a) => a + 1); }}
        />
      )}
    </div>
  );
};

const Meta = ({ course }) => (
  <div className="hp-meta">
    {course.rating && <span className="hp-meta-rating">★ {course.rating}</span>}
    <span>{course.category}</span>
    <span className="hp-dot">•</span>
    <span className="hp-cap">{course.difficulty}</span>
    {course.videoCount > 0 && (<><span className="hp-dot">•</span><span>{plural(course.videoCount, 'lesson')}</span></>)}
    {course.learners > 0 && (<><span className="hp-dot">•</span><span>{plural(course.learners, 'learner')}</span></>)}
  </div>
);

// Promo video. Mounted only when allowed; fades in once frames are playing;
// `paused` pauses it (e.g. hero scrolled off screen); `force` = the visitor
// asked for it (plays even on data saver). Falls back to the poster on error.
const PromoVideo = ({ videoId, muted = true, className = '', onFail, delayMs = 0, paused = false, force = false }) => {
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(delayMs === 0);
  const ref = useRef(null);
  useEffect(() => {
    if (delayMs === 0) return undefined;
    const t = setTimeout(() => setReady(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [paused, ready]);
  if (!videoId || failed || (SAVE_DATA && !force) || !ready) return null;
  return (
    <video
      ref={ref}
      className={`${className} hp-video ${playing ? 'is-playing' : ''}`}
      onPlaying={() => setPlaying(true)}
      src={showcaseAPI.promoStreamUrl(videoId)}
      autoPlay={!paused}
      muted={muted}
      loop
      playsInline
      preload="metadata"
      onError={() => { setFailed(true); onFail && onFail(); }}
    />
  );
};

// Mounts its children only when they come within ~1 screen of the viewport;
// until then a light skeleton keeps the page height stable.
const LazyMount = ({ children, minHeight = 260, rootMargin = '600px 0px' }) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || show) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setShow(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShow(true); io.disconnect(); } }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [show, rootMargin]);
  if (show) return children;
  return (
    <div ref={ref} className="hp-lazy-skel" style={{ minHeight }} aria-hidden="true">
      <div className="hp-skel hp-skel-title" />
      <div className="hp-lazy-skel-cards">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="hp-skel" />)}</div>
    </div>
  );
};

const CountUp = ({ value }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !value) return undefined;
    let raf;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const tick = (t) => {
        const k = Math.min(1, (t - start) / 1200);
        setN(Math.round(value * (1 - Math.pow(1 - k, 3))));
        if (k < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [value]);
  return <span ref={ref}>{n.toLocaleString('en-IN')}</span>;
};

// ----------------------------------------------------------------------------
// Course row with arrows, hover preview and (for trending) Top-10 numbers
// ----------------------------------------------------------------------------
const CourseRow = ({ row, onOpen, hoverVideo = true }) => {
  const scroller = useRef(null);
  const [hovered, setHovered] = useState(null);
  const hoverTimer = useRef(null);
  const ranked = row.key === 'trending';
  const courses = ranked ? row.courses.slice(0, 10) : row.courses;

  const scroll = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  const enter = (id) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHovered(id), 450);
  };
  const leave = () => { clearTimeout(hoverTimer.current); setHovered(null); };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  return (
    <section className="hp-row" aria-label={row.title}>
      <div className="hp-row-head">
        <h2>{ranked ? 'Top 10 trending now' : row.title}</h2>
        {row.subtitle && <p>{row.subtitle}</p>}
      </div>
      <div className="hp-row-track-wrap">
        <button className="hp-row-arrow left" onClick={() => scroll(-1)} aria-label="Scroll left">‹</button>
        <div className={`hp-row-track ${ranked ? 'ranked' : ''}`} ref={scroller}>
          {courses.map((c, i) => (
            <React.Fragment key={c.id}>
            {ranked && <span className="hp-rank" aria-hidden="true">{i + 1}</span>}
            <div
              className={`hp-card ${hovered === c.id ? 'is-hovered' : ''}`}
              onMouseEnter={() => enter(c.id)}
              onMouseLeave={leave}
            >
              <button className="hp-card-media" onClick={() => onOpen(c)} aria-label={`${c.title} by ${c.instructorName}`}>
                <Poster course={c} />
                {hovered === c.id && hoverVideo && <PromoVideo videoId={c.promoVideoId} className="hp-card-video" />}
                <div className="hp-card-badges">
                  {c.isNew && <span className="hp-badge new">NEW</span>}
                  {c.promoVideoId && <span className="hp-badge promo">▶ PREVIEW</span>}
                </div>
                <span className={`hp-price ${c.price > 0 ? '' : 'free'}`}>{formatPrice(c.price)}</span>
              </button>
              <div className="hp-card-info">
                <h3 title={c.title}>{c.title}</h3>
                <p>{c.instructorName}</p>
              </div>
              {hovered === c.id && (
                <div className="hp-card-pop">
                  <Meta course={c} />
                  <p className="hp-card-desc">{c.description}</p>
                  <div className="hp-card-actions">
                    <button className="hp-btn primary sm" onClick={() => onOpen(c)}>▶ Continue watching</button>
                    <button className="hp-btn ghost sm" onClick={() => onOpen(c)}>More info</button>
                  </div>
                </div>
              )}
            </div>
            </React.Fragment>
          ))}
        </div>
        <button className="hp-row-arrow right" onClick={() => scroll(1)} aria-label="Scroll right">›</button>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
// "Continue watching" modal: everything routes to sign in / sign up
// ----------------------------------------------------------------------------
const CourseModal = ({ course, onClose }) => {
  const navigate = useNavigate();
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div className="hp-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={course.title}>
      <div className="hp-modal" onClick={(e) => e.stopPropagation()}>
        <button className="hp-modal-close" onClick={onClose} aria-label="Close">×</button>
        <div className="hp-modal-media">
          <Poster course={course} width={960} priority />
          <PromoVideo videoId={course.promoVideoId} muted={muted} className="hp-modal-video" />
          <div className="hp-modal-media-fade" />
          {course.promoVideoId && (
            <button className="hp-mute" onClick={() => setMuted((m) => !m)} aria-label={muted ? 'Unmute preview' : 'Mute preview'}>
              {muted ? '🔇' : '🔊'}
            </button>
          )}
          <div className="hp-modal-title">
            <h2>{course.title}</h2>
            <p>by {course.instructorName}</p>
          </div>
        </div>
        <div className="hp-modal-body">
          <div className="hp-modal-main">
            <Meta course={course} />
            <p className="hp-modal-desc">{course.description}</p>
            <div className="hp-modal-lock">
              <span className="hp-lock-icon">🔒</span>
              <div>
                <strong>Continue watching on eBuddhi</strong>
                <p>Sign in or create a free account to watch this course, track your progress and earn a certificate.</p>
              </div>
            </div>
            <div className="hp-modal-actions">
              <button className="hp-btn primary" onClick={() => navigate('/login')}>▶ Continue watching</button>
              <button className="hp-btn ghost" onClick={() => navigate('/register')}>Sign up free</button>
            </div>
          </div>
          <aside className="hp-modal-side">
            <div><span>Price</span><strong>{formatPrice(course.price)}</strong></div>
            <div><span>Level</span><strong className="hp-cap">{course.difficulty}</strong></div>
            {course.duration && <div><span>Duration</span><strong>{course.duration}</strong></div>}
            <div><span>Certificate</span><strong>{course.certificate ? 'On completion' : 'Not included'}</strong></div>
            <div><span>Instructor</span><strong>{course.instructorName}</strong></div>
          </aside>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Live classes: live now, upcoming events, completed stats
// ----------------------------------------------------------------------------
const useNow = (intervalMs = 30000) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
};

const relative = (ms) => {
  const m = Math.round(Math.abs(ms) / 60000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60 ? `${m % 60}m` : ''}`.trim();
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const SessionArt = ({ s }) => (
  s.thumbnailUrl
    ? <Poster course={{ id: s.courseId, courseId: s.courseId, title: s.title, thumbnailUrl: s.thumbnailUrl }} />
    : <div className="hp-poster hp-poster-wrap"><div className="hp-poster-fallback hp-live-art"><span>{s.title}</span></div></div>
);

const LiveSection = ({ live, onJoin, onReserve }) => {
  const now = useNow();
  const liveNow = live?.now || [];
  const upcoming = live?.upcoming || [];
  const completed = live?.completedCount || 0;
  const hours = live?.completedHours || 0;
  if (!live || (!liveNow.length && !upcoming.length && !completed)) return null;

  return (
    <section className="hp-live" id="live">
      <div className="hp-row-head">
        <h2><span className={`hp-live-dot ${liveNow.length ? 'on' : ''}`} /> Live classes</h2>
        <p>Learn in real time with instructors: ask questions, get answers, live.</p>
      </div>
      <div className="hp-live-chips">
        <span className={`hp-chip ${liveNow.length ? 'red' : ''}`}>● {liveNow.length} live now</span>
        <span className="hp-chip">📅 {upcoming.length} upcoming</span>
        <span className="hp-chip">✓ {completed.toLocaleString('en-IN')} sessions completed</span>
        {hours > 0 && <span className="hp-chip">⏱ {hours.toLocaleString('en-IN')}+ hours taught live</span>}
      </div>

      <div className="hp-live-track">
        {liveNow.map((s) => (
          <article key={`l-${s.id}`} className="hp-live-card is-live">
            <div className="hp-live-media">
              <SessionArt s={s} />
              <span className="hp-live-badge"><i /> LIVE</span>
              <span className="hp-eq" aria-hidden="true"><i /><i /><i /><i /></span>
            </div>
            <div className="hp-live-body">
              <p className="hp-live-when red">Started {relative(now - Date.parse(s.startsAt))} ago · {s.durationMinutes} min</p>
              <h3>{s.title}</h3>
              <p className="hp-live-by">with <strong>{s.instructorName}</strong>{s.courseTitle ? ` · ${s.courseTitle}` : ''}</p>
              <button className="hp-btn primary sm hp-live-join" onClick={onJoin}>Join live now →</button>
            </div>
          </article>
        ))}

        {upcoming.map((s, i) => {
          const start = new Date(s.startsAt);
          const until = start.getTime() - now;
          return (
            <article key={`u-${s.id}`} className="hp-live-card">
              <div className="hp-live-date">
                <strong>{start.toLocaleDateString(undefined, { day: '2-digit' })}</strong>
                <span>{start.toLocaleDateString(undefined, { month: 'short' }).toUpperCase()}</span>
                <em>{start.toLocaleDateString(undefined, { weekday: 'short' })}</em>
              </div>
              <div className="hp-live-body">
                <p className="hp-live-when">
                  {start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
                  {' · '}{s.durationMinutes} min
                  {until > 0 && until < 7 * 864e5 && <span className={`hp-countdown ${i === 0 ? 'next' : ''}`}>in {relative(until)}</span>}
                </p>
                <h3>{s.title}</h3>
                <p className="hp-live-by">with <strong>{s.instructorName}</strong>{s.courseTitle ? ` · ${s.courseTitle}` : ''}</p>
                <div className="hp-live-foot">
                  <span className={`hp-price inline ${s.price > 0 ? '' : 'free'}`}>{formatPrice(s.price)}</span>
                  <button className="hp-btn ghost sm" onClick={onReserve}>Reserve my seat</button>
                </div>
              </div>
            </article>
          );
        })}

        {!liveNow.length && !upcoming.length && (
          <article className="hp-live-empty">
            <div className="hp-live-empty-icon">🎙️</div>
            <div>
              <h3>New live classes are scheduled regularly</h3>
              <p>
                Our instructors have already hosted <strong>{completed.toLocaleString('en-IN')} live sessions</strong>
                {hours > 0 && <> covering <strong>{hours.toLocaleString('en-IN')}+ hours</strong></>}.
                Create a free account to get notified when the next one goes live.
              </p>
            </div>
            <button className="hp-btn primary" onClick={onReserve}>Notify me</button>
          </article>
        )}
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------
const HomePage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const [heroMuted, setHeroMuted] = useState(true);
  const [openCourse, setOpenCourse] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [showAccessPopup, setShowAccessPopup] = useState(true);
  const rowsRef = useRef(null);
  const statsRef = useRef(null);
  const heroRef = useRef(null);
  const goodNetwork = useGoodNetwork();
  const pageSettled = usePageSettled();
  const [heroVisible, setHeroVisible] = useState(true);
  const [previewRequested, setPreviewRequested] = useState(false);

  useEffect(() => {
    showcaseAPI.get()
      .then((res) => setData(res.data))
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hero = data?.hero || [];
  const current = hero[heroIndex];

  // Pause the hero video while the hero is scrolled out of view.
  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setHeroVisible(e.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [current]);

  // A requested preview applies to the course it was requested for.
  useEffect(() => { setPreviewRequested(false); }, [heroIndex]);

  // Autoplay only after the first screen has loaded, on a good connection,
  // not on phones / data saver. Otherwise the visitor can tap "Watch preview".
  const autoVideo = goodNetwork && pageSettled && !IS_PHONE && !SAVE_DATA;
  const heroHasPromo = !!(current && current.promoVideoId);
  const showHeroVideo = heroHasPromo && (autoVideo || previewRequested);

  useEffect(() => {
    if (hero.length < 2 || heroPaused || openCourse || previewRequested) return undefined;
    const t = setTimeout(() => setHeroIndex((i) => (i + 1) % hero.length), HERO_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [heroIndex, hero.length, heroPaused, openCourse, previewRequested]);

  const openModal = useCallback((c) => setOpenCourse(c), []);
  const closeModal = useCallback(() => setOpenCourse(null), []);
  const stats = data?.stats || {};
  const liveNowFirst = data?.live?.now?.[0];
  const rows = useMemo(() => data?.rows || [], [data]);

  return (
    <div className="hp">
      {showAccessPopup && <AccessRequestPopup onClose={() => setShowAccessPopup(false)} />}

      {/* Navbar */}
      <header className={`hp-nav ${scrolled ? 'solid' : ''}`}>
        <Link to="/" className="hp-logo"><img src={logo} alt="eBuddhi" /></Link>
        <nav className="hp-nav-links">
          <Link to="/" className="active">Home</Link>
          <button onClick={() => rowsRef.current?.scrollIntoView({ behavior: 'smooth' })}>Explore Courses</button>
          <Link to="/pricing">Subscription &amp; Pricing</Link>
        </nav>
        <div className="hp-nav-actions">
          <button className="hp-btn ghost sm" onClick={() => navigate('/login')}>Sign In</button>
          <button className="hp-btn primary sm" onClick={() => navigate('/register')}>
            <span className="hp-label-long">Get Started Free</span><span className="hp-label-short">Join free</span>
          </button>
        </div>
      </header>

      {liveNowFirst && (
        <button className="hp-livebar" onClick={() => document.getElementById('live')?.scrollIntoView({ behavior: 'smooth' })}>
          <span className="hp-livebar-pill"><i /> LIVE NOW</span>
          <span className="hp-livebar-text"><strong>{liveNowFirst.title}</strong> with {liveNowFirst.instructorName}
            {data.live.now.length > 1 && <> · +{data.live.now.length - 1} more live</>}</span>
          <span className="hp-livebar-cta">Join →</span>
        </button>
      )}

      {/* Hero: full first screen - details left, promo video framed right */}
      <section
        ref={heroRef}
        className="hp-hero"
        onMouseEnter={() => setHeroPaused(true)}
        onMouseLeave={() => setHeroPaused(false)}
      >
        {current ? (
          <>
            <div className="hp-hero-bg" key={current.id}>
              <Poster course={current} className="hp-hero-poster" width={HERO_WIDTH} priority placeholder />
              {showHeroVideo && (
                <PromoVideo
                  videoId={current.promoVideoId}
                  muted={heroMuted}
                  className="hp-hero-video"
                  paused={!heroVisible}
                  force={previewRequested}
                />
              )}
            </div>
            <div className="hp-hero-shade" />
            <div className="hp-hero-main">
              <div className="hp-hero-content" key={`c-${current.id}`}>
                <div className="hp-hero-kicker">
                  {current.featuredSource === 'admin'
                    ? <span className="hp-badge gold">★ Featured</span>
                    : <span className="hp-badge hot">#{heroIndex + 1} Featured</span>}
                  {current.isNew && <span className="hp-badge new">NEW</span>}
                  <span className="hp-hero-cat">{current.category}</span>
                </div>
                <h1>{current.title}</h1>
                <p className="hp-hero-instructor">
                  <span className="hp-avatar">{(current.instructorName || '?').charAt(0)}</span>
                  by <strong>{current.instructorName}</strong>
                </p>
                <Meta course={current} />
                <p className="hp-hero-desc">{current.description}</p>
                <div className="hp-hero-actions">
                  <button className="hp-btn primary lg" onClick={() => openModal(current)}>▶ Continue watching</button>
                  <button className="hp-btn ghost lg" onClick={() => openModal(current)}>ⓘ More info</button>
                  {heroHasPromo && !showHeroVideo && (
                    <button className="hp-btn ghost lg hp-watch-preview" onClick={() => { setPreviewRequested(true); setHeroMuted(false); }}>
                      ▶ Watch preview
                    </button>
                  )}
                  {showHeroVideo && (
                    <button className="hp-mute" onClick={() => setHeroMuted((m) => !m)} aria-label={heroMuted ? 'Unmute' : 'Mute'}>
                      {heroMuted ? '🔇' : '🔊'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="hp-hero-strip">
              {hero.map((c, i) => (
                <button
                  key={c.id}
                  className={`hp-hero-thumb ${i === heroIndex ? 'active' : ''}`}
                  onClick={() => setHeroIndex(i)}
                  aria-label={`Show ${c.title}`}
                >
                  <Poster course={c} />
                  <span>{c.title}</span>
                  {i === heroIndex && !heroPaused && <i className="hp-hero-progress" style={{ animationDuration: `${HERO_INTERVAL_MS}ms` }} />}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="hp-hero-empty">
            <h1>Learn from experts. Anytime, anywhere.</h1>
            <p>{failed ? 'Explore hundreds of hours of expert-led courses.' : 'Loading featured courses…'}</p>
            <button className="hp-btn primary lg" onClick={() => navigate('/register')}>Get Started Free</button>
          </div>
        )}

        <button
          className={`hp-scroll-cue ${scrolled ? 'is-hidden' : ''}`}
          onClick={() => statsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          aria-label="Scroll down to explore"
        >
          <span className="hp-mouse"><i /></span>
          <span className="hp-scroll-text">Scroll to explore</span>
          <span className="hp-chevron" aria-hidden="true" />
        </button>
      </section>

      {/* Stats */}
      {stats.courses > 0 && (
        <section ref={statsRef} className={`hp-stats ${stats.liveSessionsCompleted > 0 ? 'five' : ''}`}>
          <div><strong><CountUp value={stats.courses} />+</strong><span>Expert-led courses</span></div>
          <div><strong><CountUp value={stats.instructors} />+</strong><span>Instructors</span></div>
          <div><strong><CountUp value={stats.learners} />+</strong><span>Active learners</span></div>
          {stats.liveSessionsCompleted > 0 && (
            <div><strong><CountUp value={stats.liveSessionsCompleted} />+</strong><span>Live sessions held</span></div>
          )}
          <div><strong><CountUp value={stats.categories} />+</strong><span>Categories</span></div>
        </section>
      )}

      <LiveSection live={data?.live} onJoin={() => navigate('/login')} onReserve={() => navigate('/register')} />

      {/* Rows */}
      <div className="hp-rows" ref={rowsRef}>
        {!data && !failed && [1, 2].map((i) => (
          <section className="hp-row" key={i}>
            <div className="hp-row-head"><div className="hp-skel hp-skel-title" /></div>
            <div className="hp-row-track">{[1, 2, 3, 4, 5].map((j) => <div key={j} className="hp-card hp-skel" />)}</div>
          </section>
        ))}
        {rows.map((row, i) => (
          <React.Fragment key={row.key}>
            {i === 0
              ? <CourseRow row={row} onOpen={openModal} hoverVideo={goodNetwork && !SAVE_DATA} />
              : <LazyMount><CourseRow row={row} onOpen={openModal} hoverVideo={goodNetwork && !SAVE_DATA} /></LazyMount>}
            {i === 1 && (
              <section className="hp-band">
                <div>
                  <h2>Learning is free. Pay only for the courses you love.</h2>
                  <p>Create a free account, explore every course, watch previews, and buy only what fits your goals.</p>
                </div>
                <div className="hp-band-actions">
                  <button className="hp-btn primary" onClick={() => navigate('/register')}>Create free account</button>
                  <Link to="/pricing" className="hp-btn ghost">See pricing</Link>
                </div>
              </section>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Top instructors */}
      {data?.instructors?.length > 0 && (
        <section className="hp-instructors">
          <div className="hp-row-head">
            <h2>Learn from top instructors</h2>
            <p>Practitioners who teach what they do every day</p>
          </div>
          <div className="hp-instructor-grid">
            {data.instructors.map((ins) => (
              <button key={ins.id} className="hp-instructor" onClick={() => navigate('/login')}>
                {ins.avatar ? (
                  <img
                    src={ins.avatar.startsWith('/static/profiles/') ? `${API_ORIGIN}/api/showcase/avatar/${ins.id}?w=480` : assetUrl(ins.avatar)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      const img = e.currentTarget;
                      const original = assetUrl(ins.avatar);
                      if (img.src !== original) img.src = original; else img.style.display = 'none';
                    }}
                  />
                ) : null}
                <span className="hp-instructor-initial">{ins.name.charAt(0)}</span>
                <strong>{ins.name}</strong>
                <span>{plural(ins.courses, 'course')} · {plural(ins.learners, 'learner')}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* How it works + teach */}
      <section className="hp-how">
        <div className="hp-how-steps">
          <h2>How eBuddhi works</h2>
          {[
            ['1', 'Sign up free', 'Create your account in under a minute. No card required.'],
            ['2', 'Explore & preview', 'Browse every course and watch free previews before you decide.'],
            ['3', 'Learn & get certified', 'Enrol in free courses instantly or buy the ones you love, track progress and earn certificates.'],
          ].map(([n, t, d]) => (
            <div key={n} className="hp-how-step">
              <span>{n}</span>
              <div><strong>{t}</strong><p>{d}</p></div>
            </div>
          ))}
        </div>
        <div className="hp-teach">
          <span className="hp-teach-icon">🎓</span>
          <h3>Share your expertise</h3>
          <p>Publish courses, run live sessions, and earn from every enrolment. Start free and grow with Pro.</p>
          <Link to="/pricing" className="hp-btn primary">Teach on eBuddhi</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="hp-footer">
        <div className="hp-footer-top">
          <div className="hp-footer-brand">
            <img src={logo} alt="eBuddhi" />
            <p>Empowering learners and educators worldwide</p>
          </div>
          <div className="hp-footer-links">
            <div>
              <h4>Explore</h4>
              <button onClick={() => rowsRef.current?.scrollIntoView({ behavior: 'smooth' })}>Courses</button>
              <Link to="/pricing">Subscription &amp; Pricing</Link>
              <Link to="/register">Become an instructor</Link>
            </div>
            <div>
              <h4>Account</h4>
              <Link to="/login">Sign in</Link>
              <Link to="/register">Create account</Link>
              <Link to="/forgot-password">Forgot password</Link>
            </div>
            <div>
              <h4>Policy</h4>
              <a href="mailto:support@ebuddhi.com">Support</a>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms-of-use">Terms of Use</Link>
              <Link to="/refund-and-cancellation-policy">Refund and Cancellation Policy</Link>
              <Link to="/accessibility-statement">Accessibility Statement</Link>
            </div>
          </div>
        </div>
        <p className="hp-footer-bottom">&copy; {new Date().getFullYear()} eBuddhi. All rights reserved.</p>
      </footer>

      {openCourse && <CourseModal course={openCourse} onClose={closeModal} />}
    </div>
  );
};

export default HomePage;
